import { useEffect } from 'react'
import { isRouteErrorResponse, Link, useRouteError } from 'react-router'
import { LogoMark } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { reportError } from '@/lib/sentry'

export function FullPageLoader() {
  return (
    <div className="grid min-h-dvh place-items-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <LogoMark tone="dark" className="size-10 dark:hidden" />
        <LogoMark tone="light" className="hidden size-10 dark:block" />
        <Spinner label="Loading Arva SEO" />
      </div>
    </div>
  )
}

export function PageSkeleton() {
  return (
    <div className="grid gap-6" aria-busy="true" aria-label="Loading page">
      <div className="grid gap-3">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-10 w-80 max-w-full" />
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        <Skeleton className="h-28 rounded-lg" />
        <Skeleton className="h-28 rounded-lg" />
        <Skeleton className="h-28 rounded-lg" />
      </div>
      <Skeleton className="h-64 rounded-lg" />
    </div>
  )
}

export function NotFoundPage({ homeHref = '/' }: { homeHref?: string }) {
  return (
    <div className="grid min-h-[60dvh] place-items-center px-6">
      <div className="grid max-w-md gap-4 text-center">
        <p className="eyebrow">404</p>
        <h1 className="display text-4xl text-ink">This page doesn&apos;t exist</h1>
        <p className="text-sm text-ink-muted">
          It may have moved, or you may not have access to it. Workspaces you aren&apos;t a member of are
          hidden.
        </p>
        <div>
          <Button asChild variant="ink">
            <Link to={homeHref}>Go to your dashboard</Link>
          </Button>
        </div>
      </div>
    </div>
  )
}

export function RouteErrorBoundary() {
  const error = useRouteError()
  const notFound = isRouteErrorResponse(error) && error.status === 404
  const chunkFailed =
    error instanceof Error &&
    /dynamically imported module|Importing a module script failed/i.test(error.message)

  // A missing page or an old tab after a deploy isn't a bug; everything else goes to Sentry.
  useEffect(() => {
    if (!notFound && !chunkFailed) reportError(error)
  }, [error, notFound, chunkFailed])

  if (notFound) return <NotFoundPage />

  return (
    <div className="grid min-h-[60dvh] place-items-center px-6">
      <div role="alert" className="grid max-w-md gap-4 text-center">
        <p className="eyebrow">Unexpected error</p>
        <h1 className="display text-4xl text-ink">Something went wrong</h1>
        <p className="text-sm text-ink-muted">
          {chunkFailed
            ? 'A new version of Arva SEO is available. Reload to continue.'
            : 'The page failed to load. Reloading usually fixes it; your data is safe.'}
        </p>
        <div>
          <Button variant="ink" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </div>
      </div>
    </div>
  )
}
